import React, { useState } from "react";
import { Gamepad2, Zap } from "lucide-react";
import { toast } from "sonner";

const MIDI_CONTROLS = [
  { id: "deck-a-play",  label: "Deck A · Play/Pause", type: "button" },
  { id: "deck-a-cue",   label: "Deck A · Cue (to start)", type: "button" },
  { id: "deck-b-play",  label: "Deck B · Play/Pause", type: "button" },
  { id: "deck-b-cue",   label: "Deck B · Cue (to start)", type: "button" },
  { id: "xfader",       label: "Crossfader", type: "fader" },
  { id: "music-vol",    label: "Music volume", type: "fader" },
  { id: "master-vol",   label: "Master volume", type: "fader" },
  { id: "mic-toggle",   label: "Mic ON/OFF", type: "button" },
];

/**
 * MidiPanel — connect a MIDI DJ controller and bind its faders / buttons.
 * Extracted from RadioHostStudio.
 */
export default function MidiPanel({ midi }) {
  const [open, setOpen] = useState(false);

  if (!midi.supported) {
    return (
      <div data-testid="radio-host-midi" className="rounded-lg border border-white/10 bg-black/30 p-3 text-white/50 text-xs">
        <div className="flex items-center gap-2 font-black uppercase tracking-wider text-white/70">
          <Gamepad2 className="w-3.5 h-3.5 text-fuchsia-300" /> MIDI Controller
        </div>
        <div className="mt-1">Your browser doesn't expose Web MIDI. Use Chrome, Edge, or Opera on desktop for hardware controller support.</div>
      </div>
    );
  }

  const anyDevice = midi.devices.length > 0;

  return (
    <div data-testid="radio-host-midi" className="rounded-lg border border-white/10 bg-black/30">
      <div
        data-testid="midi-toggle"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
        className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer select-none"
      >
        <Gamepad2 className="w-3.5 h-3.5 text-fuchsia-300" />
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-[11px] uppercase tracking-wider">MIDI Controller</div>
          <div className="text-white/50 text-[10px] truncate">
            {anyDevice ? `Connected: ${midi.devices.map((d) => d.name).join(" · ")}` : "No device detected — plug in and click Enable"}
          </div>
        </div>
        {anyDevice ? (
          <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-[9px] font-black px-2 py-0.5 rounded-full">
            <Zap className="w-2.5 h-2.5" /> LIVE
          </span>
        ) : (
          <button
            data-testid="midi-enable"
            onClick={(e) => { e.stopPropagation(); midi.enable().then((ok) => { if (!ok) toast.error("MIDI access denied — check browser permissions", { id: "midi" }); }); }}
            className="bg-fuchsia-500 text-black font-black text-[10px] px-2 py-1 rounded"
          >
            Enable
          </button>
        )}
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-1">
          {midi.lastMsg && (
            <div className="text-[9px] text-white/40 font-mono">
              Last: {midi.lastMsg.kind.toUpperCase()} ch{midi.lastMsg.channel + 1} · #{midi.lastMsg.number} · {midi.lastMsg.value.toFixed(2)}
            </div>
          )}
          {MIDI_CONTROLS.map((c) => {
            const map = midi.mappings[c.id];
            const learning = midi.learningFor === c.id;
            return (
              <div
                key={c.id}
                data-testid={`midi-row-${c.id}`}
                className={`flex items-center gap-2 text-[10px] rounded px-1.5 py-1 ${learning ? "bg-fuchsia-500/20 border border-fuchsia-500/50 animate-pulse" : "bg-white/5"}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{c.label}</div>
                  <div className="text-white/40 font-mono text-[9px] truncate">
                    {learning ? "Touch the control on your device…" : map ? `${map.kind.toUpperCase()} ch${map.channel + 1} · #${map.number}` : "Unbound"}
                  </div>
                </div>
                {learning ? (
                  <button data-testid={`midi-cancel-${c.id}`} onClick={midi.cancelLearn} className="bg-white/10 hover:bg-white/20 text-white text-[9px] font-black px-2 py-0.5 rounded">Cancel</button>
                ) : (
                  <>
                    <button data-testid={`midi-learn-${c.id}`} onClick={() => midi.startLearn(c.id)} className="bg-fuchsia-500/25 hover:bg-fuchsia-500 hover:text-black text-fuchsia-200 text-[9px] font-black px-2 py-0.5 rounded">Learn</button>
                    {map && <button data-testid={`midi-clear-${c.id}`} onClick={() => midi.clearMapping(c.id)} title="Clear binding" className="text-white/40 hover:text-red-400 px-1">×</button>}
                  </>
                )}
              </div>
            );
          })}
          <div className="text-white/40 text-[9px] italic pt-1">
            Tip: works with any class-compliant controller (Pioneer DDJ, Numark Mixtrack, Hercules, Launchpad…). Mappings save per-station.
          </div>
        </div>
      )}
    </div>
  );
}
